import {useFormContext} from "react-hook-form";

import {Card} from "@/components/card/Card.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {DatePickerField} from "@/components/form/fields/DatePickerField.tsx";
import {NumberInputField} from "@/components/form/fields/NumberInputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {TagsField} from "@/components/form/fields/TagsField/TagsField.tsx";
import {RequiredFieldsNote} from "@/components/form/requiredFieldsNote/RequiredFieldsNote.tsx";
import {SupportLink} from "@/components/formSubtext/subComponents/SupportLink/SupportLink.tsx";
import {Input} from "@/components/input/Input.tsx";
import {Section} from "@/components/section/Section.tsx";
import {timezoneAbbr} from "@/lib/format.tsx";
import {
  DurationUnit,
  DurationUnitDisplay,
  FrequencyType,
  FrequencyTypeDisplay,
} from "@/pages/maintenance/maintenance-create/tabs/general/MaintenanceScheduleGeneralTab.types.ts";
import {CustomRepeatSection} from "@/pages/maintenance/maintenance-create/tabs/general/subComponents/CustomRepeatSection/CustomRepeatSection.tsx";
import {EndSection} from "@/pages/maintenance/maintenance-create/tabs/general/subComponents/EndSection/EndSection.tsx";
import {MaintenanceNameField} from "@/pages/maintenance/maintenance-create/tabs/general/subComponents/MaintenanceNameField/MaintenanceNameField.tsx";
import {MonthlyRepeatOptions} from "@/pages/maintenance/maintenance-create/tabs/general/subComponents/MonthlyRepeatOptions/MonthlyRepeatOptions.tsx";
import {WeeklyRepeatOptions} from "@/pages/maintenance/maintenance-create/tabs/general/subComponents/WeeklyRepeatOptions/WeeklyRepeatOptions.tsx";

import styles from "./MaintenanceScheduleGeneralTab.module.scss";

export const MaintenanceScheduleGeneralTab = () => {
  const {watch, register} = useFormContext();
  const {user} = useCurrentUserContext();
  const timezone = user!.account!.timezone;
  const frequencyType = watch("frequency_type");

  return (
    <div className="d-flex flex-column gap-5 mb-4">
      <div>
        <span className={"pulseTextBodySmall pulse-color-gray-700"}>
          Schedule planned maintenance windows to suppress Alerts and notify users when maintenance starts and ends.{" "}
          <SupportLink
            href={"https://support.uptime.com/hc/en-us/articles/22713676097180-Check-Maintenance-Windows"}
            text={"View our documentation"}
          />
        </span>
      </div>

      <div className="d-flex flex-column gap-4">
        <RequiredFieldsNote spacing={"none"} />

        <MaintenanceNameField />

        <Card variant={"white"} className={"p-3"}>
          <SwitchField
            name="pause_checks_during_maintenance"
            label="Pause Checks during Maintenance"
            description="When turned off, checks still run but alerts are suppressed"
            labelPosition={"left"}
          />
        </Card>
      </div>

      <Section title={"Schedule"} description={"When the maintenance window starts and duration"} />

      <div className="d-flex flex-column gap-4">
        <DatePickerField
          name="start_date"
          label="Start Date/Time"
          mode="single"
          required
          datePickerV3Props={{
            hasTimeSelect: true,
            resetDefault: new Date(),
          }}
          helpText={`Times are in ${timezoneAbbr(timezone)}.`}
        />

        <div className={"d-flex gap-4"}>
          <div className={"d-flex gap-3 flex-1"}>
            <NumberInputField
              name="duration_value"
              label="Duration"
              required
              className={styles.durationInput}
              input={<Input type="number" min={1} {...register("duration_value", {valueAsNumber: true})} />}
              formSubtextClassName={"text-nowrap"}
            />
            <SelectField
              name="duration_unit"
              label="&nbsp;"
              className={styles.durationUnit}
              options={Object.values(DurationUnit).map((unit) => ({
                value: unit,
                label: DurationUnitDisplay[unit],
              }))}
            />
          </div>

          <div className={"d-flex flex-1"}>
            <SelectField
              className={styles.frequencySelect}
              name="frequency_type"
              label="Frequency"
              required
              tooltip="Choose how often this Maintenance Schedule should repeat."
              options={Object.values(FrequencyType).map((freq) => ({
                value: freq,
                label: FrequencyTypeDisplay[freq],
              }))}
            />
          </div>
        </div>

        {frequencyType !== FrequencyType.DONT_REPEAT && (
          <Card variant={"white"} className={"d-flex flex-column gap-3"}>
            {frequencyType === FrequencyType.WEEKLY && <WeeklyRepeatOptions />}

            {frequencyType === FrequencyType.MONTHLY && <MonthlyRepeatOptions />}

            {frequencyType === FrequencyType.CUSTOM && <CustomRepeatSection />}

            {(frequencyType === FrequencyType.CUSTOM ||
              frequencyType === FrequencyType.MONTHLY ||
              frequencyType === FrequencyType.WEEKLY) && <Divider />}
            <EndSection />
          </Card>
        )}
      </div>

      <Section title={"Optional"} />

      <TagsField
        name="tags"
        label="Schedule Tags"
        placeholder="Select tags..."
        tooltip="Assign tags to this maintenance schedule for organizing and filtering your schedules list. This does not affect which checks are included — to assign checks, use the Affected Checks tab."
      />
    </div>
  );
};
